import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BlocSwitcher, seanceIconClass } from "@/components/shared";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Pencil, Loader2, FileText, Link2, GripVertical, BookOpen, ChevronUp, ChevronDown,
  ListChecks, Check, X as XIcon, Lock, Unlock,
} from "lucide-react";
import { input, btn, btnGhost } from "./adminShared";

function MoveButtons({ idx, count, onMove, testIdPrefix }) {
  return (
    <div className="flex flex-col shrink-0">
      <button type="button" disabled={idx === 0} onClick={() => onMove(idx, idx - 1)} data-testid={`${testIdPrefix}-up-${idx}`}
        className="h-4 w-5 flex items-center justify-center text-slate-400 hover:text-orange-600 disabled:opacity-20 disabled:hover:text-slate-400">
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button type="button" disabled={idx === count - 1} onClick={() => onMove(idx, idx + 1)} data-testid={`${testIdPrefix}-down-${idx}`}
        className="h-4 w-5 flex items-center justify-center text-slate-400 hover:text-orange-600 disabled:opacity-20 disabled:hover:text-slate-400">
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function SequenceTab() {
  const [sequences, setSequences] = useState([]);
  const [seqId, setSeqId] = useState(null);
  const [seq, setSeq] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [docBusy, setDocBusy] = useState(false);
  const [linkForm, setLinkForm] = useState({ label: "", url: "" });
  const [courseLinkForm, setCourseLinkForm] = useState({ label: "", url: "" });
  const [dragDocIdx, setDragDocIdx] = useState(null);
  const [dragLinkIdx, setDragLinkIdx] = useState(null);
  const [dragCourseLinkIdx, setDragCourseLinkIdx] = useState(null);
  const [dragSubIdx, setDragSubIdx] = useState(null);
  const [subForm, setSubForm] = useState("");
  const [editingSubId, setEditingSubId] = useState(null);
  const [editingSubTitle, setEditingSubTitle] = useState("");

  useEffect(() => {
    api.get("/sequences").then(({ data }) => { setSequences(data); setSeqId((p) => p || data[0]?.id); });
  }, []);

  const load = () => seqId && api.get(`/sequences/${seqId}`).then(({ data }) => setSeq(data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSeq(null); load(); }, [seqId]);

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/seances/${edit.id}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setEdit((prev) => ({ ...prev, documents: [...(prev.documents || []), data] }));
      toast.success("Document ajouté ✓");
      load();
    } catch { toast.error("Échec de l'envoi du document"); }
    finally { setDocBusy(false); e.target.value = ""; }
  };

  const deleteDoc = async (fileId) => {
    try {
      await api.delete(`/admin/seances/${edit.id}/documents/${fileId}`);
      setEdit((prev) => ({ ...prev, documents: (prev.documents || []).filter((d) => d.id !== fileId) }));
      toast.success("Document supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const addLink = async () => {
    if (!linkForm.label.trim() || !linkForm.url.trim()) { toast.error("Libellé et lien obligatoires"); return; }
    try {
      const { data } = await api.post(`/admin/seances/${edit.id}/links`, { label: linkForm.label, url: linkForm.url });
      setEdit((prev) => ({ ...prev, links: [...(prev.links || []), data] }));
      setLinkForm({ label: "", url: "" });
      toast.success("Lien ajouté ✓");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteLink = async (linkId) => {
    try {
      await api.delete(`/admin/seances/${edit.id}/links/${linkId}`);
      setEdit((prev) => ({ ...prev, links: (prev.links || []).filter((l) => l.id !== linkId) }));
      toast.success("Lien supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const reorderDocs = (fromIdx, toIdx) => {
    if (fromIdx === null || fromIdx === toIdx) return;
    const list = [...edit.documents];
    const [item] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, item);
    setEdit((prev) => ({ ...prev, documents: list }));
    setDragDocIdx(null);
    api.put(`/admin/seances/${edit.id}/documents/reorder`, { order: list.map((d) => d.id) })
      .then(load)
      .catch(() => { toast.error("Échec de la réorganisation"); load(); setEdit((prev) => ({ ...prev, documents: edit.documents })); });
  };

  const reorderLinks = (fromIdx, toIdx) => {
    if (fromIdx === null || fromIdx === toIdx) return;
    const list = [...edit.links];
    const [item] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, item);
    setEdit((prev) => ({ ...prev, links: list }));
    setDragLinkIdx(null);
    api.put(`/admin/seances/${edit.id}/links/reorder`, { order: list.map((l) => l.id) })
      .then(load)
      .catch(() => { toast.error("Échec de la réorganisation"); load(); setEdit((prev) => ({ ...prev, links: edit.links })); });
  };

  const addCourseLink = async () => {
    if (!courseLinkForm.label.trim() || !courseLinkForm.url.trim()) { toast.error("Libellé et lien obligatoires"); return; }
    try {
      const { data } = await api.post(`/admin/seances/${edit.id}/course-links`, { label: courseLinkForm.label, url: courseLinkForm.url });
      setEdit((prev) => ({ ...prev, course_links: [...(prev.course_links || []), data] }));
      setCourseLinkForm({ label: "", url: "" });
      toast.success("Lien de cours ajouté ✓");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteCourseLink = async (linkId) => {
    try {
      await api.delete(`/admin/seances/${edit.id}/course-links/${linkId}`);
      setEdit((prev) => ({ ...prev, course_links: (prev.course_links || []).filter((l) => l.id !== linkId) }));
      toast.success("Lien supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const reorderCourseLinks = (fromIdx, toIdx) => {
    if (fromIdx === null || fromIdx === toIdx) return;
    const list = [...edit.course_links];
    const [item] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, item);
    setEdit((prev) => ({ ...prev, course_links: list }));
    setDragCourseLinkIdx(null);
    api.put(`/admin/seances/${edit.id}/course-links/reorder`, { order: list.map((l) => l.id) })
      .then(load)
      .catch(() => { toast.error("Échec de la réorganisation"); load(); setEdit((prev) => ({ ...prev, course_links: edit.course_links })); });
  };

  const addSubSeance = async () => {
    if (!subForm.trim()) { toast.error("Titre de l'étape obligatoire"); return; }
    try {
      const { data } = await api.post(`/admin/seances/${edit.id}/sub-seances`, { title: subForm });
      setEdit((prev) => ({ ...prev, sub_seances: [...(prev.sub_seances || []), data] }));
      setSubForm("");
      toast.success("Étape ajoutée ✓");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const startEditSub = (sub) => { setEditingSubId(sub.id); setEditingSubTitle(sub.title); };
  const cancelEditSub = () => { setEditingSubId(null); setEditingSubTitle(""); };

  const saveEditSub = async () => {
    if (!editingSubTitle.trim()) { toast.error("Titre de l'étape obligatoire"); return; }
    try {
      await api.put(`/admin/seances/${edit.id}/sub-seances/${editingSubId}`, { title: editingSubTitle });
      setEdit((prev) => ({ ...prev, sub_seances: prev.sub_seances.map((s) => (s.id === editingSubId ? { ...s, title: editingSubTitle } : s)) }));
      cancelEditSub();
      toast.success("Étape modifiée ✓");
      load();
    } catch { toast.error("Erreur"); }
  };

  const deleteSubSeance = async (subId) => {
    try {
      await api.delete(`/admin/seances/${edit.id}/sub-seances/${subId}`);
      setEdit((prev) => ({ ...prev, sub_seances: (prev.sub_seances || []).filter((s) => s.id !== subId) }));
      toast.success("Étape supprimée");
      load();
    } catch { toast.error("Erreur"); }
  };

  const reorderSubSeances = (fromIdx, toIdx) => {
    if (fromIdx === null || fromIdx === toIdx) return;
    const list = [...edit.sub_seances];
    const [item] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, item);
    setEdit((prev) => ({ ...prev, sub_seances: list }));
    setDragSubIdx(null);
    api.put(`/admin/seances/${edit.id}/sub-seances/reorder`, { order: list.map((s) => s.id) })
      .then(load)
      .catch(() => { toast.error("Échec de la réorganisation"); load(); setEdit((prev) => ({ ...prev, sub_seances: edit.sub_seances })); });
  };

  const setCurrent = async (order) => {
    await api.put(`/admin/sequences/${seqId}`, { current_seance_order: order });
    toast.success(`Séance ${order} marquée « en cours »`);
    load();
  };

  const toggleLock = async (s) => {
    try {
      await api.put(`/admin/seances/${s.id}/lock`, { locked: !s.locked });
      toast.success(s.locked ? "Séance déverrouillée ✓" : "Séance verrouillée — dépôt de preuve bloqué pour les élèves");
      load();
    } catch { toast.error("Erreur"); }
  };

  const saveSeance = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/seances/${edit.id}`, {
        title: edit.title, objective: edit.objective, content: edit.content,
        content_html: edit.content_html || "", level_target: edit.level_target,
        competency_code: edit.competency_code,
      });
      toast.success("Séance mise à jour ✓");
      setEdit(null);
      load();
    } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  const meta = sequences.find((s) => s.id === seqId);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <p className="text-slate-500 font-medium">{meta?.title} — {meta?.subtitle}</p>
        <BlocSwitcher sequences={sequences} value={seqId} onChange={setSeqId} />
      </div>
      <p className="text-sm text-slate-400 font-medium mb-5">Choisis la séance « en cours » : les précédentes passent en « vue », les suivantes en « à venir ».</p>
      {!seq ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 text-orange-400 animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {seq.seances.map((s) => (
            <div key={s.id} className={`rounded-3xl border shadow-sm p-5 flex items-center gap-4 ${s.status === "en_cours" ? "bg-orange-50 border-orange-200" : "bg-white border-slate-100"}`} data-testid={`admin-seance-${s.order}`}>
              <div className={`h-10 w-10 rounded-2xl flex items-center justify-center font-extrabold shrink-0 ${seanceIconClass(s.status)}`}>{s.order}</div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-slate-400">{s.competency_code} · {s.level_target}</p>
                <p className="font-bold text-slate-800 truncate flex items-center gap-1.5">
                  {s.title}
                  {s.locked && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-full shrink-0" data-testid={`locked-badge-${s.order}`}>
                      <Lock className="h-2.5 w-2.5" /> Verrouillée
                    </span>
                  )}
                </p>
              </div>
              <button onClick={() => toggleLock(s)} data-testid={`toggle-lock-${s.order}`}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${s.locked ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                {s.locked ? <><Unlock className="h-3.5 w-3.5" /> Déverrouiller</> : <><Lock className="h-3.5 w-3.5" /> Verrouiller</>}
              </button>
              <button onClick={() => setEdit(s)} className={btnGhost} data-testid={`edit-seance-${s.order}`}><Pencil className="h-4 w-4" /> Éditer</button>
              {s.status !== "en_cours" && (
                <button onClick={() => setCurrent(s.order)} data-testid={`set-current-${s.order}`} className="text-sm font-bold text-orange-600 hover:text-orange-700 whitespace-nowrap">Marquer en cours</button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">Éditer la séance</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={saveSeance} className="space-y-3 mt-2">
              <input className={input} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Code compétence</label>
                  <input className={input} value={edit.competency_code} onChange={(e) => setEdit({ ...edit, competency_code: e.target.value })} placeholder="ex. C2.4" data-testid="seance-competency-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Niveau visé</label>
                  <input className={input} value={edit.level_target} onChange={(e) => setEdit({ ...edit, level_target: e.target.value })} placeholder="Averti / Expert" />
                </div>
              </div>
              <textarea className={input + " min-h-[70px]"} value={edit.objective} onChange={(e) => setEdit({ ...edit, objective: e.target.value })} placeholder="Objectif" />
              <textarea className={input + " min-h-[140px]"} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} placeholder="Contenu (texte simple, puces avec -)" />
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <label className="block text-sm font-bold text-slate-700 mb-1 inline-flex items-center gap-1.5"><ListChecks className="h-4 w-4 text-orange-500" /> Étapes de la séance (sous-séances, optionnel)</label>
                <p className="text-xs text-slate-400 font-medium mb-2">Divise cette séance en plusieurs petites étapes : chaque étape aura sa propre preuve + autoévaluation côté élève. Laisse vide pour garder un dépôt de preuve classique.</p>
                {(edit.sub_seances || []).length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {edit.sub_seances.map((sub, idx) => (
                      <div
                        key={sub.id}
                        draggable={editingSubId !== sub.id}
                        onDragStart={() => setDragSubIdx(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => reorderSubSeances(dragSubIdx, idx)}
                        data-testid={`sub-seance-drag-${sub.id}`}
                        className={`flex items-center gap-2 bg-white border border-slate-100 rounded-xl px-3 py-2 ${editingSubId === sub.id ? "" : "cursor-grab active:cursor-grabbing"} ${dragSubIdx === idx ? "opacity-40" : ""}`}
                      >
                        <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
                        <MoveButtons idx={idx} count={edit.sub_seances.length} onMove={reorderSubSeances} testIdPrefix="sub-seance-move" />
                        <span className="h-6 w-6 rounded-lg bg-orange-100 text-orange-600 text-xs font-extrabold flex items-center justify-center shrink-0">{idx + 1}</span>
                        {editingSubId === sub.id ? (
                          <>
                            <input
                              className={input + " flex-1 py-1.5"}
                              value={editingSubTitle}
                              onChange={(e) => setEditingSubTitle(e.target.value)}
                              data-testid={`sub-seance-edit-input-${sub.id}`}
                              autoFocus
                            />
                            <button type="button" onClick={saveEditSub} data-testid={`sub-seance-save-${sub.id}`} className="h-7 w-7 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 flex items-center justify-center shrink-0"><Check className="h-3.5 w-3.5" /></button>
                            <button type="button" onClick={cancelEditSub} data-testid={`sub-seance-cancel-${sub.id}`} className="h-7 w-7 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center shrink-0"><XIcon className="h-3.5 w-3.5" /></button>
                          </>
                        ) : (
                          <>
                            <span className="text-xs font-bold text-slate-700 truncate flex-1">{sub.title}</span>
                            <button type="button" onClick={() => startEditSub(sub)} data-testid={`sub-seance-edit-${sub.id}`} className="h-7 w-7 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center shrink-0"><Pencil className="h-3.5 w-3.5" /></button>
                            <button type="button" onClick={() => deleteSubSeance(sub.id)} data-testid={`delete-sub-seance-${sub.id}`} className="h-7 w-7 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <input className={input + " flex-1"} placeholder="Titre de l'étape (ex. Étape 1 : préparation)" value={subForm} onChange={(e) => setSubForm(e.target.value)} data-testid="sub-seance-title-input" />
                  <button type="button" onClick={addSubSeance} data-testid="add-sub-seance-btn" className="inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-4 py-2.5 font-bold text-sm shrink-0"><Plus className="h-4 w-4" /> Ajouter une étape</button>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <label className="block text-sm font-bold text-slate-700 mb-1">Liens « Accéder au cours » (optionnel)</label>
                <p className="text-xs text-slate-400 font-medium mb-2">Ajoute un ou plusieurs liens vers ton cours (page web, Genially, Google Slides…). Chacun s'affiche comme un bouton principal « Accéder au cours » pour l'élève. Glisse-dépose pour réordonner.</p>
                {(edit.course_links || []).length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {edit.course_links.map((l, idx) => (
                      <div
                        key={l.id}
                        draggable
                        onDragStart={() => setDragCourseLinkIdx(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => reorderCourseLinks(dragCourseLinkIdx, idx)}
                        data-testid={`course-link-drag-${l.id}`}
                        className={`flex items-center gap-2 bg-white border border-slate-100 rounded-xl px-3 py-2 cursor-grab active:cursor-grabbing ${dragCourseLinkIdx === idx ? "opacity-40" : ""}`}
                      >
                        <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
                        <MoveButtons idx={idx} count={edit.course_links.length} onMove={reorderCourseLinks} testIdPrefix="course-link-move" />
                        <BookOpen className="h-4 w-4 text-orange-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-slate-700 truncate">{l.label}</p>
                          <p className="text-[11px] text-slate-400 truncate">{l.url}</p>
                        </div>
                        <button type="button" onClick={() => deleteCourseLink(l.id)} data-testid={`delete-course-link-${l.id}`} className="h-7 w-7 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <input className={input + " sm:flex-1"} placeholder="Libellé (ex. Cours partie 1)" value={courseLinkForm.label} onChange={(e) => setCourseLinkForm({ ...courseLinkForm, label: e.target.value })} data-testid="course-link-label-input" />
                  <input className={input + " sm:flex-[2]"} placeholder="https://…" value={courseLinkForm.url} onChange={(e) => setCourseLinkForm({ ...courseLinkForm, url: e.target.value })} data-testid="course-link-url-input" />
                  <button type="button" onClick={addCourseLink} data-testid="add-course-link-btn" className="inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-4 py-2.5 font-bold text-sm shrink-0"><Plus className="h-4 w-4" /> Ajouter</button>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <label className="block text-sm font-bold text-slate-700 mb-1">Cours en HTML (optionnel)</label>
                <p className="text-xs text-slate-400 font-medium mb-2">Colle ici le code HTML de ton cours (créé par Claude par ex.). S'il est rempli, il remplace le « Contenu » ci-dessus pour l'élève. Laisse vide pour garder le texte simple.</p>
                <textarea
                  className={input + " min-h-[160px] font-mono text-xs"}
                  value={edit.content_html || ""}
                  onChange={(e) => setEdit({ ...edit, content_html: e.target.value })}
                  placeholder="<h2>Mon cours</h2><p>…</p>"
                  data-testid="seance-html-input"
                />
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <label className="block text-sm font-bold text-slate-700 mb-1">Documents à télécharger (élèves)</label>
                <p className="text-xs text-slate-400 font-medium mb-2">Ajoute des fichiers (PDF, Word, images…) que l'élève pourra télécharger en ouvrant ce cours. Glisse-dépose pour réordonner.</p>
                {(edit.documents || []).length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {edit.documents.map((d, idx) => (
                      <div
                        key={d.id}
                        draggable
                        onDragStart={() => setDragDocIdx(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => reorderDocs(dragDocIdx, idx)}
                        data-testid={`doc-drag-${d.id}`}
                        className={`flex items-center gap-2 bg-white border border-slate-100 rounded-xl px-3 py-2 cursor-grab active:cursor-grabbing ${dragDocIdx === idx ? "opacity-40" : ""}`}
                      >
                        <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
                        <MoveButtons idx={idx} count={edit.documents.length} onMove={reorderDocs} testIdPrefix="doc-move" />
                        <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                        <span className="text-xs font-bold text-slate-700 truncate flex-1">{d.name}</span>
                        <button type="button" onClick={() => deleteDoc(d.id)} data-testid={`delete-doc-${d.id}`} className="h-7 w-7 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm font-bold text-orange-600 hover:text-orange-700">
                  {docBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  {docBusy ? "Envoi…" : "Ajouter un document"}
                  <input type="file" className="hidden" onChange={uploadDoc} disabled={docBusy} data-testid="seance-doc-input" />
                </label>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <label className="block text-sm font-bold text-slate-700 mb-1">Liens du cours (élèves)</label>
                <p className="text-xs text-slate-400 font-medium mb-2">Ajoute des liens (vidéo YouTube, Genially, Quizlet, Google Doc…) avec un libellé. L'élève les ouvre en un clic depuis le cours. Glisse-dépose pour réordonner.</p>
                {(edit.links || []).length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {edit.links.map((l, idx) => (
                      <div
                        key={l.id}
                        draggable
                        onDragStart={() => setDragLinkIdx(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => reorderLinks(dragLinkIdx, idx)}
                        data-testid={`link-drag-${l.id}`}
                        className={`flex items-center gap-2 bg-white border border-slate-100 rounded-xl px-3 py-2 cursor-grab active:cursor-grabbing ${dragLinkIdx === idx ? "opacity-40" : ""}`}
                      >
                        <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
                        <MoveButtons idx={idx} count={edit.links.length} onMove={reorderLinks} testIdPrefix="link-move" />
                        <Link2 className="h-4 w-4 text-slate-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-slate-700 truncate">{l.label}</p>
                          <p className="text-[11px] text-slate-400 truncate">{l.url}</p>
                        </div>
                        <button type="button" onClick={() => deleteLink(l.id)} data-testid={`delete-link-${l.id}`} className="h-7 w-7 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <input className={input + " sm:flex-1"} placeholder="Libellé (ex. Vidéo)" value={linkForm.label} onChange={(e) => setLinkForm({ ...linkForm, label: e.target.value })} data-testid="link-label-input" />
                  <input className={input + " sm:flex-[2]"} placeholder="https://…" value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} data-testid="link-url-input" />
                  <button type="button" onClick={addLink} data-testid="add-link-btn" className="inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-4 py-2.5 font-bold text-sm shrink-0"><Plus className="h-4 w-4" /> Ajouter</button>
                </div>
              </div>
              <DialogFooter><button type="submit" disabled={busy} className={btn}>{busy ? "…" : "Enregistrer"}</button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
